// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The cabinet's flipper buttons, drawn as the bats they drive (M3-50, 08 §5.2a).
//
// **A button shaped like the thing it moves is the strongest signifier available here.** The band
// under the playfield already draws two buttons; making each one the silhouette of *its own*
// flipper — tapered, and angled the way that bat rests on the glass — removes the last piece of
// guessing. Left button, left bat, same angle, and pressing it swings the drawing through the same
// arc the real one swings through.
//
// **Read from the machine, not from a stylesheet.** `restAngle`, `sweep`, `length`, `radiusPivot`
// and `radiusTip` are all in the definition, and a machine built in the Builder may have flippers
// at any angle at all. A hard-coded 21° would be right for NEON NOVA and quietly wrong for
// everything else — the same class of mistake as a stylesheet holding its own copy of the flipper
// band's height.
//
// The two frames this converts between are worth stating once, because getting it backwards is the
// kind of bug that looks like a design choice:
//
// - **Playfield**: `x` right, `y` *up the table*, angles anticlockwise, radians.
// - **Screen**: `x` right, `y` *down*, CSS rotation clockwise, degrees.
//
// A playfield direction `θ` is a screen direction `−θ`. So NEON NOVA's left bat at `−0.372 rad`
// (−21.3°) points down-and-right on the glass and is drawn rotated **+21.3°** — pivot up and to the
// left, tip down and to the right, which is exactly how it looks through the glass.

/**
 * How much of a zone the drawn bat takes (M3-51).
 *
 * **The first version was 88% and it overwhelmed the machine.** The band is 18% of a phone screen
 * directly under the playfield, and two bats filling nearly all of it read as the main event with
 * a pinball table above them. The drawing is not the target — the whole 156 × 129 zone is the hit
 * rectangle — so it can be well under half the band's area and still be a control nobody could
 * miss. Two thirds leaves a clear margin around each bat and lets the machine stay the loudest
 * thing on screen, which is the right order.
 */
export const BAT_WIDTH_FRACTION = 0.66

/** …and never wider than this, so a tablet's band is not two enormous bats. */
export const BAT_MAX_PX = 176

/**
 * One width for both bats, in CSS pixels.
 *
 * **Taken from the narrower of the two zones**, because in landscape the right one gives up the
 * plunger's column and a percentage width therefore drew a 220 px left bat beside a 156 px right
 * one. Two flippers are a matched pair on every machine ever built; a mismatched pair reads as a
 * mistake before it reads as a layout.
 */
export function batWidthFor(leftWidth: number, rightWidth: number, laneWidth: number): number {
  const usable = Math.min(leftWidth, rightWidth - laneWidth > 0 ? rightWidth - laneWidth : rightWidth)
  return Math.max(1, Math.round(Math.min(usable * BAT_WIDTH_FRACTION, BAT_MAX_PX)))
}

/** How a bat is drawn, in a form the stylesheet can use without knowing any of the above. */
export interface Bat {
  /** The silhouette: two circles joined by their external tangents, as one closed path. */
  readonly path: string
  readonly viewBox: string
  /**
   * Where the pivot sits across the element, 0…1, for `transform-origin`.
   *
   * A bat rotates about its pivot and nothing else. Rotating about the centre of the box would
   * swing the tip *and* the pivot, which reads as a bat that has come loose.
   */
  readonly pivot: number
  /** Screen rotation, degrees, at rest… */
  readonly restDegrees: number
  /** …and held, which is the same arc the real bat sweeps. */
  readonly pressedDegrees: number
}

export interface CabinetBats {
  readonly left: Bat
  readonly right: Bat
}

/**
 * What to draw when the machine has no flippers to ask.
 *
 * A machine can legitimately have none — a Builder draft mid-edit, a fixture — and a band with no
 * buttons on it would be worse than a band with generic ones. These are NEON NOVA's numbers, which
 * are ordinary numbers for a modern flipper.
 */
const FALLBACK = {
  length: 0.0635,
  radiusPivot: 0.0105,
  radiusTip: 0.0055,
  restAngle: -0.372292,
  sweep: 0.90757,
} as const

interface FlipperDevice {
  readonly kind?: unknown
  readonly handedness?: unknown
  readonly pivot?: unknown
  readonly length?: unknown
  readonly radiusPivot?: unknown
  readonly radiusTip?: unknown
  readonly restAngle?: unknown
  readonly sweep?: unknown
}

const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

/** Normalise to (−180, 180], so a rotation never takes the long way round. */
export function wrapDegrees(degrees: number): number {
  const d = ((degrees + 180) % 360 + 360) % 360 - 180
  return d === -180 ? 180 : d
}

/**
 * The bat silhouette: the convex hull of the pivot disc and the tip disc.
 *
 * Two arcs and the two external tangents between them, which is the actual shape of a flipper bat
 * rather than an approximation of one. `α = asin((Rp − Rt) / L)` is the angle the tangent makes
 * with the centre line; both tangent points sit at `90° + α` from the centre line on their own
 * circle, which is what makes the straight edges touch each disc rather than cut across it.
 *
 * Built as one path rather than as two `<circle>`s and a `<polygon>`, because a group of three
 * shapes has seams — invisible under a flat fill and obvious the moment the button gains an
 * outline or a drop shadow, which is the whole visual idea here.
 *
 * `tip` is which way the bat points: `1` for a left bat drawn along `+x`, `−1` for a right one
 * drawn along `−x`, so that each pivot ends up on the outer edge of its own button.
 */
export function batPath(
  length: number,
  radiusPivot: number,
  radiusTip: number,
  tip: 1 | -1,
): string {
  const s = 1000 // machine metres to path units, so the numbers read as tens rather than as 0.0105
  const L = length * s
  const rp = radiusPivot * s
  const rt = radiusTip * s
  // Clamped: a tip wider than the pivot has no external tangent at this angle, and a definition is
  // allowed to be strange without the band throwing.
  const ratio = L > 0 ? Math.min(Math.max((rp - rt) / L, -1), 1) : 0
  const a = Math.asin(ratio)
  const cos = Math.cos(Math.PI / 2 + a)
  const sin = Math.sin(Math.PI / 2 + a)

  const f = (n: number): string => (Math.round(n * 100) / 100).toString()
  const px = rp * cos * tip
  const py = rp * sin
  const tx = (L + rt * cos) * tip
  const ty = rt * sin
  // Sweep flags are mirrored with the bat: reversing `x` reverses which way round the outline goes.
  const sweep = tip === 1 ? 0 : 1
  return [
    `M ${f(px)} ${f(py)}`,
    `L ${f(tx)} ${f(ty)}`,
    `A ${f(rt)} ${f(rt)} 0 1 ${sweep} ${f(tx)} ${f(-ty)}`,
    `L ${f(px)} ${f(-py)}`,
    `A ${f(rp)} ${f(rp)} 0 0 ${sweep} ${f(px)} ${f(py)}`,
    'Z',
  ].join(' ')
}

function batFrom(device: FlipperDevice | undefined, side: 'left' | 'right'): Bat {
  const length = num(device?.length, FALLBACK.length)
  const radiusPivot = num(device?.radiusPivot, FALLBACK.radiusPivot)
  const radiusTip = num(device?.radiusTip, FALLBACK.radiusTip)
  const restAngle = num(device?.restAngle, side === 'left' ? FALLBACK.restAngle : Math.PI - FALLBACK.restAngle)
  const sweep = num(device?.sweep, FALLBACK.sweep)

  const s = 1000
  const L = length * s
  const rp = radiusPivot * s
  const rt = radiusTip * s
  const width = L + rp + rt
  const tip = side === 'left' ? 1 : -1

  // The left bat is drawn along +x and the right along −x, so each pivot lands on the outer edge of
  // its own button. `base` is therefore the direction the *drawing* already points, and the
  // rotation is whatever turns that into the direction the bat actually rests at.
  const base = side === 'left' ? 0 : 180
  const degrees = (radians: number): number => wrapDegrees(-(radians * 180) / Math.PI - base)
  // A left bat sweeps anticlockwise on the playfield and a right one clockwise. `handedness` is
  // what the core reads to decide this, and it is what decides it here.
  const active = side === 'left' ? restAngle + sweep : restAngle - sweep

  return {
    path: batPath(length, radiusPivot, radiusTip, tip),
    viewBox: `${tip === 1 ? -rp : -(L + rt)} ${-rp} ${width} ${2 * rp}`,
    pivot: width > 0 ? (tip === 1 ? rp / width : (L + rt) / width) : 0.5,
    restDegrees: degrees(restAngle),
    pressedDegrees: degrees(active),
  }
}

/**
 * The two bats a player's thumbs drive.
 *
 * **The lowest flipper of each handedness**, which is the one the button is about. NEON NOVA has
 * three: a main pair at `y = −0.426` and an upper-left one at `y = 0.06`, and the left button
 * drives both of them — `Game` maps a button to *every* bat with that handedness. Drawing the
 * upper bat's angle on the left button would be honest about the wiring and useless to the player,
 * who is aiming with the one at the bottom of the glass.
 */
export function batsFrom(definition: unknown): CabinetBats {
  const devices = (definition as { devices?: unknown })?.devices
  const flippers = Array.isArray(devices)
    ? (devices as FlipperDevice[]).filter((d) => d?.kind === 'flipper')
    : []
  const lowest = (side: 'left' | 'right'): FlipperDevice | undefined => {
    let best: FlipperDevice | undefined
    let bestY = Number.POSITIVE_INFINITY
    for (const d of flippers) {
      if (d.handedness !== side) continue
      const pivot = d.pivot
      const y = Array.isArray(pivot) && typeof pivot[1] === 'number' ? pivot[1] : 0
      if (y < bestY) {
        bestY = y
        best = d
      }
    }
    return best
  }
  return { left: batFrom(lowest('left'), 'left'), right: batFrom(lowest('right'), 'right') }
}
